import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete'
import MobileSheetAutoComplete from '../../../components/ui/MobileSheetAutoComplete'
import { useEmployeeSearch } from '../../minutes/hooks/useEmployeeSearch'
import type { EmployeePick } from '../../minutes/types'
import { AGENDA_SOURCE } from '../api/agendaEmployeeSource'
import { personName, sicilKey, type AgendaParticipant } from '../types'
import '../../minutes/minutes.css'

/** Seçici satırı → gövdeye giden anlık görüntü. */
function toParticipant(p: EmployeePick): AgendaParticipant {
  return {
    employeeId: p.employeeId,
    firstName: p.firstName,
    lastName: p.lastName,
    businessMail: p.businessMail,
    jobTitleTr: p.jobTitleTr,
    jobTitleEn: p.jobTitleEn,
  }
}

interface Props {
  /** İçteki arama kutusunun `id`'si — çağıran `<label htmlFor>` bağlar. */
  inputId: string
  value: AgendaParticipant[]
  onChange: (next: AgendaParticipant[]) => void
  placeholder: string
  invalid?: boolean
  /** Hata kutusunun `id`'si — içteki girdinin `aria-describedby`'ı. */
  describedBy?: string
}

/**
 * Katılımcı seçici (İK çalışanı, sicil). Seçilen kişi çip listesine eklenir, kutu temizlenir; aynı
 * sicil ikinci kez eklenmez ve önerilmez.
 */
export default function AgendaParticipantsField({
  inputId,
  value,
  onChange,
  placeholder,
  invalid,
  describedBy,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const { suggestions: raw, search } = useEmployeeSearch(AGENDA_SOURCE)
  const [query, setQuery] = useState('')
  const selected = useMemo(() => new Set(value.map((p) => sicilKey(p.employeeId))), [value])
  // Referans sabit kalmalı (useMemo): AutoComplete her yeni `suggestions` referansında paneli yeniden
  // açar; her çizimde yeni dizi sonsuz güncelleme döngüsüyle ekranı çökertiyordu (jsdom'da görünmez).
  const suggestions = useMemo(
    () => raw.filter((s) => !selected.has(sicilKey(s.employeeId))),
    [raw, selected],
  )

  const complete = (e: AutoCompleteCompleteEvent) => search(e.query)

  // Yazarken string gelir; klavyeyle vurgulanan nesnede adı göster (seçim onSelect'te).
  const onQueryChange = (e: AutoCompleteChangeEvent) => {
    const v: unknown = e.value
    setQuery(typeof v === 'string' ? v : ((v as EmployeePick | null)?.fullName ?? ''))
  }

  const onSelect = (e: AutoCompleteSelectEvent) => {
    const picked = e.value as EmployeePick | null
    if (picked?.employeeId && !selected.has(sicilKey(picked.employeeId))) {
      onChange([...value, toParticipant(picked)])
    }
    setQuery('')
  }

  const remove = (employeeId: string) => onChange(value.filter((p) => p.employeeId !== employeeId))
  const jobTitle = (p: AgendaParticipant) =>
    (i18n.language === 'en' ? p.jobTitleEn : p.jobTitleTr) ?? p.jobTitleTr ?? ''

  return (
    <div className="agenda-participants-field">
      <MobileSheetAutoComplete<EmployeePick>
        value={query}
        suggestions={suggestions}
        completeMethod={complete}
        field="fullName"
        delay={300}
        minLength={2}
        placeholder={placeholder}
        onChange={onQueryChange}
        onSelect={onSelect}
        inputId={inputId}
        panelClassName="minutes-participant-panel"
        className={invalid ? 'p-invalid minutes-participant-input' : 'minutes-participant-input'}
        itemTemplate={(item: EmployeePick) => (
          <div className="minutes-emp-item">
            <span className="minutes-emp-name">{item.fullName}</span>
            {item.jobTitle && <span className="minutes-emp-title">{item.jobTitle}</span>}
            <span className="minutes-emp-sicil">{item.employeeId}</span>
          </div>
        )}
        /* Tekil modda ARIA hedefi `input.root`'tur. */
        pt={{
          input: {
            root: { 'aria-invalid': invalid ? 'true' : 'false', 'aria-describedby': describedBy },
          },
        }}
      />
      {value.length > 0 && (
        <ul className="agenda-participant-chips">
          {value.map((p) => {
            const name = personName(p)
            const removeLabel = t('agenda.form.removeParticipant', { name })
            return (
              <li key={p.employeeId} className="agenda-participant-chip">
                <span className="agenda-participant-name">{name}</span>
                <span className="agenda-muted">{jobTitle(p)}</span>
                <button
                  type="button"
                  className="agenda-participant-remove"
                  aria-label={removeLabel}
                  title={removeLabel}
                  onClick={() => remove(p.employeeId)}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
